import { useEffect, useState, type SyntheticEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiClientError } from '../api/client';
import { messageFor } from '../api/messages';
import {
  pairingApi,
  type InspectedStation,
  type PairedRegister,
  type PairingInspect,
} from '../api/stations';
import { useAuth } from '../auth/AuthContext';
import { currentPerson } from '../auth/currentPerson';
import { Alert, Button, Card, TextField } from '../components/ui';
import { useFragmentToken } from '../hooks/useFragmentToken';
import { tryAcquireRegisterLock } from '../pos/db';
import { clientDeviceId } from '../pos/deviceId';
import {
  describeLocalRegister,
  forgetLocalRegister,
  rememberPosEvent,
  type LocalRegisterInfo,
} from '../pos/sessions';
import { PosStore } from '../pos/store';
import type { RegisterMeta } from '../pos/types';

/**
 * Das Kopplungs-Token (aus dem QR-Code, im Fragment der Adresse) wartet im sessionStorage dieses Tabs, nie in Adresse,
 * Abfrage oder `next`: So überlebt es die Anmeldung, ohne in Verlauf oder Protokollen aufzutauchen.
 */
const STORAGE_KEY = 'barso.pairing';

interface Stored {
  token: string;
  stationId: string | null;
}

function readStored(): Stored | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    return typeof parsed.token === 'string'
      ? { token: parsed.token, stationId: parsed.stationId ?? null }
      : null;
  } catch {
    return null;
  }
}

function writeStored(value: Stored | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Speicher gesperrt (privater Modus): der Link funktioniert weiterhin, nur nicht über die Anmeldung hinweg
  }
}

/** Adresse der Kasse nach der Kopplung: Anmeldung über den Basar, Schnellzugang ohne Konto über die Geräte-Seite. */
export const posPath = (eventId: string, mode: 'login' | 'name'): string =>
  mode === 'login' ? `/basare/${eventId}/kasse` : `/kasse-geraet/${eventId}`;

/**
 * Legt die neu gekoppelte Kasse im lokalen Speicher des Geräts an. Voraussetzung (vor der Kopplung geprüft, `prepareDevice`):
 * Dort ist noch keine Kasse und der Tab hält die Sperre dieses Basars.
 */
async function storeRegister(paired: PairedRegister): Promise<void> {
  const owner = paired.mode === 'name' ? null : currentPerson();
  const store = await PosStore.open(`barso-pos-${paired.eventId}`);
  try {
    const meta: RegisterMeta = {
      eventId: paired.eventId,
      registerId: paired.id,
      label: paired.label,
      labelSalt: 0,
      sessionToken: paired.sessionToken,
      sessionExpiresAt: paired.sessionExpiresAt,
      mode: paired.mode,
      stationId: paired.stationId,
      // Eine Kasse mit Konto gehört der Person, die sie koppelt (Zuordnung des Kassierers)
      ...(owner === null ? {} : { ownerUserId: owner }),
    };
    await store.setRegister(meta);
  } finally {
    store.close();
  }
  rememberPosEvent(paired.eventId, paired.mode === 'name');
}

const DEVICE_ERRORS: Record<string, string> = {
  STATION_IN_USE:
    'An diesem Kassenplatz ist schon ein Gerät gekoppelt. Wählen Sie einen anderen Platz oder bitten Sie das Team, das Gerät abzukoppeln.',
  PIN_INVALID: 'Die PIN stimmt nicht.',
  PIN_LOCKED: 'Zu viele falsche PINs von diesem Netz. Bitte später erneut versuchen.',
  PAIRING_LOCKED:
    'Die Kopplung dieses Kassenplatzes ist nach zu vielen falschen PINs vorübergehend gesperrt. Bitte das Team fragen.',
  SALES_CLOSED:
    'Im Moment lässt sich keine Kasse koppeln (nur während Annahme, Verkauf und Rückgabe).',
};

function pairingMessage(error: unknown, byLogin: boolean): string {
  if (error instanceof ApiClientError) {
    const known = DEVICE_ERRORS[error.code];
    if (known !== undefined) return known;
    if (error.status === 404) {
      return byLogin
        ? 'Das hat nicht geklappt. Entweder ist der Code nicht mehr gültig oder Ihnen fehlt im Basar die Rolle Kasse. Fragen Sie das Team (Einladungslink mit Rolle Kasse oder Rolle im Team).'
        : 'Dieser Code ist nicht mehr gültig oder der Schnellzugang ist für diesen Platz nicht erlaubt.';
    }
  }
  return messageFor(error);
}

/**
 * Seite „Kasse koppeln“ (`/kasse-koppeln#token=…`, öffentlich): zeigt Basar und Kassenplatz zum QR-Code. Mit Anmeldung (Konto
 * mit Kassenrecht) oder, wenn der Platz es erlaubt, mit Name (und PIN) ohne Konto. Der zentrale Code zeigt alle Plätze.
 */
export function PairingPage() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const fragmentToken = useFragmentToken();
  const [stored] = useState<Stored | null>(() =>
    fragmentToken === null ? readStored() : { token: fragmentToken, stationId: null },
  );
  const token = stored?.token ?? null;
  useEffect(() => {
    if (fragmentToken !== null) writeStored({ token: fragmentToken, stationId: null });
  }, [fragmentToken]);

  const [view, setView] = useState<PairingInspect | null>(null);
  const [unknown, setUnknown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(stored?.stationId ?? null);
  const [local, setLocal] = useState<LocalRegisterInfo | null>(null);
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (token === null) return;
    const guard = { cancelled: false };
    pairingApi.inspect(token).then(
      (loaded) => {
        if (guard.cancelled) return;
        setView(loaded);
        // Bei einem Platz-Code ist der Platz klar; beim zentralen Code gilt die zuvor gemerkte Wahl, wenn sie noch frei ist
        const only = loaded.kind === 'station' ? loaded.stations[0] : undefined;
        if (only !== undefined) setSelected(only.id);
        void describeLocalRegister(loaded.eventId).then((info) => {
          if (!guard.cancelled) setLocal(info);
        });
      },
      (caught: unknown) => {
        if (guard.cancelled) return;
        if (caught instanceof ApiClientError && caught.status === 404) setUnknown(true);
        else setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [token]);

  if (token === null || unknown) {
    return (
      <div className="narrow stack">
        <h1>Kasse koppeln</h1>
        <Alert kind="error">
          {token === null
            ? 'Dieser Link ist unvollständig. Bitte scannen Sie den QR-Code noch einmal.'
            : 'Dieser Code ist nicht (mehr) gültig. Er wurde vielleicht erneuert, oder der Basar nimmt gerade keine Kassen an. Bitte fragen Sie das Team nach dem aktuellen Aushang.'}
        </Alert>
      </div>
    );
  }
  if (view === null) {
    return (
      <div className="narrow stack">
        <h1>Kasse koppeln</h1>
        {error === null ? (
          <p role="status">Einen Moment bitte …</p>
        ) : (
          <Alert kind="error">{error}</Alert>
        )}
      </div>
    );
  }

  const station: InspectedStation | undefined = view.stations.find(
    (entry) => entry.id === selected,
  );
  const blockedByLocal = local !== null && local.exists;

  async function finish(paired: PairedRegister) {
    try {
      await storeRegister(paired);
    } catch (caught) {
      setError(
        `Die Kasse wurde gekoppelt, ließ sich aber auf diesem Gerät nicht speichern: ${messageFor(caught)} Bitte noch einmal versuchen; gelingt es nicht, das Team bitten, das Gerät abzukoppeln.`,
      );
      return;
    }
    writeStored(null);
    void navigate(posPath(paired.eventId, paired.mode), { replace: true });
  }

  /**
   * Vor jeder Kopplung: Auf dem Gerät darf noch keine Kasse dieses Basars liegen und dieser Tab muss die Sperre halten. Sonst
   * wäre der Platz am Server belegt, ohne dass sich die Kasse hier speichern ließe. Gibt die Freigabe zurück oder `null`.
   */
  async function prepareDevice(eventId: string): Promise<(() => void) | null> {
    const info = await describeLocalRegister(eventId);
    if (info.exists) {
      setLocal(info);
      return null;
    }
    const release = await tryAcquireRegisterLock(eventId);
    if (release === null) {
      setError(
        'Die Kasse dieses Basars ist in einem anderen Tab geöffnet. Bitte dort schließen und erneut versuchen.',
      );
      return null;
    }
    return release;
  }

  /** Prüft das Gerät, koppelt und speichert; die Sperre bleibt bis zum Ende gehalten. */
  async function pairOnDevice(eventId: string, claim: () => Promise<PairedRegister>) {
    const release = await prepareDevice(eventId);
    if (release === null) return;
    try {
      await finish(await claim());
    } finally {
      release();
    }
  }

  async function byLogin(event: SyntheticEvent) {
    event.preventDefault();
    if (station === undefined || view === null || token === null) return;
    setBusy(true);
    setError(null);
    try {
      await pairOnDevice(view.eventId, () =>
        pairingApi.claimLogin(view.eventId, station.id, {
          token,
          clientDeviceId: clientDeviceId(),
        }),
      );
    } catch (caught) {
      setError(pairingMessage(caught, true));
    } finally {
      setBusy(false);
    }
  }

  async function byName(event: SyntheticEvent) {
    event.preventDefault();
    if (station === undefined || view === null || token === null) return;
    setBusy(true);
    setError(null);
    try {
      await pairOnDevice(view.eventId, () =>
        pairingApi.claimName({
          token,
          ...(view.kind === 'hub' ? { stationId: station.id } : {}),
          name: name.trim(),
          ...(station.pinRequired ? { pin: pin.trim() } : {}),
          clientDeviceId: clientDeviceId(),
        }),
      );
    } catch (caught) {
      setError(pairingMessage(caught, false));
    } finally {
      setBusy(false);
    }
  }

  async function createNew(event: SyntheticEvent) {
    event.preventDefault();
    if (view === null || token === null) return;
    setBusy(true);
    setError(null);
    try {
      await pairOnDevice(view.eventId, () =>
        pairingApi.newStation(view.eventId, {
          token,
          label: newLabel.trim(),
          clientDeviceId: clientDeviceId(),
        }),
      );
    } catch (caught) {
      setError(
        caught instanceof ApiClientError && caught.status === 403
          ? 'Neue Kassenplätze legt das Team an, das den Basar verwaltet. Bitte wählen Sie oben einen vorhandenen Platz.'
          : pairingMessage(caught, true),
      );
    } finally {
      setBusy(false);
    }
  }

  async function removeLocal() {
    if (view === null) return;
    if (
      !window.confirm(
        'Die Kasse auf diesem Gerät entfernen? Alle Verkäufe sind schon übertragen. Danach lässt sich eine neue Kasse koppeln.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await forgetLocalRegister(view.eventId);
      setLocal(await describeLocalRegister(view.eventId));
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="narrow stack stack--lg">
      <h1>Kasse koppeln</h1>
      <p>
        {view.organizationName} · <strong>{view.eventTitle}</strong>
      </p>
      {error !== null && <Alert kind="error">{error}</Alert>}

      {blockedByLocal && (
        <Card>
          <div className="stack">
            <Alert kind="info">
              {`Auf diesem Gerät ist bereits Kasse ${local.label ?? ''} geöffnet.`.replace(
                '  ',
                ' ',
              )}{' '}
              {local.unsent > 0 || local.unknown
                ? 'Erst Verkäufe senden und abmelden.'
                : 'Alle Verkäufe sind übertragen.'}
            </Alert>
            <div className="row">
              <Link className="btn" to={posPath(view.eventId, local.mode ?? 'login')}>
                Zur Kasse
              </Link>
              {local.unsent === 0 && !local.unknown && (
                <Button variant="secondary" busy={busy} onClick={() => void removeLocal()}>
                  Kasse von diesem Gerät entfernen
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {!blockedByLocal && (
        <>
          {view.kind === 'hub' && (
            <section className="stack" aria-labelledby="platz">
              <h2 id="platz">Kassenplatz wählen</h2>
              {view.stations.length === 0 && (
                <p className="muted">Noch kein Kassenplatz angelegt.</p>
              )}
              <ul className="list">
                {view.stations.map((entry) => (
                  <li key={entry.id}>
                    <div>
                      <strong>{entry.label}</strong>
                      <div className="muted">
                        {entry.occupied ? 'belegt' : 'frei'}
                        {entry.quickAccess === 'name' &&
                          (entry.pinRequired
                            ? ' · Schnellzugang mit PIN'
                            : ' · Schnellzugang ohne PIN')}
                      </div>
                    </div>
                    <Button
                      small
                      variant={selected === entry.id ? 'primary' : 'secondary'}
                      disabled={entry.occupied}
                      aria-pressed={selected === entry.id}
                      onClick={() => {
                        setSelected(entry.id);
                        setError(null);
                        writeStored({ token, stationId: entry.id });
                      }}
                    >
                      {selected === entry.id ? 'Gewählt' : 'Wählen'}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {station !== undefined && (
            <section className="stack" aria-labelledby="zugang">
              <h2 id="zugang">{`Kasse „${station.label}“ öffnen`}</h2>
              {station.occupied && (
                <Alert kind="error">
                  {DEVICE_ERRORS['STATION_IN_USE'] ?? 'Dieser Platz ist belegt.'}
                </Alert>
              )}
              {!station.occupied && (
                <>
                  <Card>
                    <div className="stack">
                      <h3>Mit meinem Konto anmelden</h3>
                      {status === 'loading' ? (
                        <p role="status">Einen Moment bitte …</p>
                      ) : status === 'authenticated' ? (
                        <form className="stack" onSubmit={(event) => void byLogin(event)}>
                          <p className="muted">
                            Dafür brauchen Sie im Basar die Rolle Kasse. Sie wird nie über den
                            QR-Code vergeben, sondern vom Team.
                          </p>
                          <Button type="submit" busy={busy} disabled={local === null}>
                            Kasse öffnen
                          </Button>
                        </form>
                      ) : (
                        <div className="stack">
                          <p className="muted">
                            Melden Sie sich an. Danach kommen Sie hierher zurück.
                          </p>
                          <div className="row">
                            <Link
                              className="btn"
                              to={`/anmelden?next=${encodeURIComponent('/kasse-koppeln')}`}
                            >
                              Anmelden
                            </Link>
                            <Link
                              className="btn btn--secondary"
                              to={`/registrieren?next=${encodeURIComponent('/kasse-koppeln')}`}
                            >
                              Konto anlegen
                            </Link>
                          </div>
                          <p className="muted">
                            Nach dem Bestätigen Ihrer E-Mail scannen Sie den QR-Code bitte erneut.
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>

                  {station.quickAccess === 'name' && (
                    <Card>
                      <form className="stack" onSubmit={(event) => void byName(event)} noValidate>
                        <h3>Ohne Konto mit Name</h3>
                        <TextField
                          label="Ihr Name oder Kürzel"
                          hint="Erscheint in der Verkaufsübersicht des Basars."
                          maxLength={40}
                          required
                          autoComplete="off"
                          value={name}
                          onChange={(change) => {
                            setName(change.target.value);
                          }}
                        />
                        {station.pinRequired && (
                          <TextField
                            label="PIN des Kassenplatzes"
                            hint="Sechs Ziffern, vom Team."
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={6}
                            required
                            value={pin}
                            onChange={(change) => {
                              setPin(change.target.value);
                            }}
                          />
                        )}
                        <p className="muted">
                          Datenschutz: Ihr Name dient nur der Zuordnung der Verkäufe dieser Kasse
                          und dem Protokoll (Art. 6 Abs. 1 lit. f DSGVO, Nachvollziehbarkeit der
                          Kasse). Er wird 30 Tage nach dem Basar durch „Gerät N“ ersetzt. Es
                          entsteht kein Konto und keine Rolle: Dieses Gerät kann nur Artikel laden
                          und die Verkäufe dieser Kasse übertragen.
                        </p>
                        <Button
                          type="submit"
                          busy={busy}
                          disabled={
                            local === null ||
                            name.trim() === '' ||
                            (station.pinRequired && !/^\d{6}$/.test(pin.trim()))
                          }
                        >
                          Kasse öffnen
                        </Button>
                      </form>
                    </Card>
                  )}
                </>
              )}
            </section>
          )}

          {view.kind === 'hub' && status === 'authenticated' && (
            <section className="stack" aria-labelledby="neu">
              <h2 id="neu">Neue Kasse anlegen</h2>
              <Card>
                <form className="stack" onSubmit={(event) => void createNew(event)} noValidate>
                  <p className="muted">
                    Nur für das Team, das den Basar verwaltet (Rolle Manager): legt einen neuen
                    Kassenplatz an und öffnet ihn auf diesem Gerät. Wer nur kassiert, wählt oben
                    einen vorhandenen Platz.
                  </p>
                  <TextField
                    label="Name der neuen Kasse"
                    maxLength={60}
                    required
                    value={newLabel}
                    onChange={(change) => {
                      setNewLabel(change.target.value);
                    }}
                  />
                  <Button
                    type="submit"
                    busy={busy}
                    disabled={local === null || newLabel.trim() === ''}
                  >
                    Neue Kasse anlegen und öffnen
                  </Button>
                </form>
              </Card>
            </section>
          )}
        </>
      )}
    </div>
  );
}
